import { ArrowLeft, Box, Rss, BookOpen } from 'lucide-react';
import { getAllBlogPosts, getFeaturedPosts } from '../data/blogPosts';
import BlogCard from './BlogCard';

export default function BlogList() {
  const allPosts = getAllBlogPosts();
  const featuredPosts = getFeaturedPosts();
  const regularPosts = allPosts.filter(post => !post.featured);

  return (
    <div className="min-h-screen bg-[#FAFBFC]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-gray-200 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#FF6B6B] rounded-lg flex items-center justify-center rotate-3">
              <Box className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-gray-900">JsonToonCraft</span>
          </a>
          <div className="flex items-center gap-3">
            <a
              href="/docs"
              className="hidden sm:flex items-center gap-2 px-4 py-2 text-[#1A535C] hover:bg-gray-100 rounded-lg text-sm font-medium transition-all"
            >
              <BookOpen className="w-4 h-4" />
              API Docs
            </a>
            <a
              href="/playground"
              className="px-5 py-2 bg-[#FF6B6B] text-white rounded-lg text-sm font-medium hover:bg-[#ff5252] transition-all hover:scale-105"
            >
              Playground
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-6xl mx-auto">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </a>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FF6B6B]/10 rounded-full text-sm text-[#FF6B6B] font-medium mb-4">
                <Rss className="w-4 h-4" />
                Blog
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Developer Insights &amp; Guides
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl">
                Tips, tutorials, and best practices for modern development. Learn about JSON conversion, LLM optimization, and developer productivity.
              </p>
            </div>

            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span className="font-medium text-gray-900">{allPosts.length}</span>
              <span>article{allPosts.length !== 1 ? 's' : ''} published</span>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Posts */}
      {featuredPosts.length > 0 && (
        <section className="px-6 pb-12">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-6">
              Featured
            </h2>
            <div className="space-y-6">
              {featuredPosts.map((post) => (
                <BlogCard key={post.slug} post={post} featured />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Posts */}
      {regularPosts.length > 0 && (
        <section className="px-6 pb-20">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-6">
              All Articles
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {regularPosts.map((post) => (
                <BlogCard key={post.slug} post={post} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Empty State */}
      {allPosts.length === 0 && (
        <section className="px-6 pb-20">
          <div className="max-w-6xl mx-auto text-center py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <BookOpen className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No articles yet</h3>
            <p className="text-gray-600">Check back soon for new content!</p>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-12 px-6 bg-white border-t border-gray-200">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#FF6B6B] rounded-lg flex items-center justify-center rotate-3">
              <Box className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-gray-900">JsonToonCraft</span>
          </div>
          <p className="text-sm text-gray-600">
            Built with love by{' '}
            <a
              href="https://krishcdbry.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#FF6B6B] font-medium hover:underline"
            >
              Krish
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
